import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Form, Spin, Input, Select, Switch, DatePicker, Flex } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { SyncOutlined } from '@ant-design/icons';
import { getExpenseHeader } from '../../../api/api.ts';
import { useAppSelector } from 'src/redux/hooks.ts';
import SearchableSelect from '../../../components/SearchableSelect/index.tsx';
import { cleanInput, validateInput } from '../../../utils/textUtils.ts';
import { useFormValidation } from '../hooks/useFormValidation';
import ExpenseEntries from './ExpenseEntries.tsx';
import { ExpenseFormFieldsProps, ExpenseRowItem, ProfileInfo, FETCH_DATA_TYPES } from '../types';

/**
 * ExpenseFormFields Component
 *
 * Handles the core form logic and data initialization for expense forms.
 *
 * Responsibilities:
 * - Expense data initialization and fetching
 * - Form field population and validation
 * - Expense row data transformation
 * - Loading state management
 * - Integration between form fields and entries
 */
export const ExpenseFormFields: React.FC<ExpenseFormFieldsProps> = ({
  permissions,
  profile,
  expenseSettings,
  recordToEdit,
  isMyExpense,
  isExpenseOwnerChangeAvailable,
  isPrivateCard,
  onOwnerSwitchChange,
  onPrivateCardChange,
  form,
  submitting,
  approving,
  status,
  headerId,
  setFormChanged,
  initializationLoading,
  debouncedFetch,
  dimensions,
  projects,
  costTypes,
  companyCards,
  xgl,
  users,
  files,
  setFiles,
  setsubmitterInfo,
}) => {
  const { t } = useTranslation();
  const { createRequiredOnlyRules, createConditionalRules } =
    useFormValidation();
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  // Local state
  const [loadingExpense, setLoadingExpense] = useState(false);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [prevOwner, setPrevOwner] = useState<string>(profile.user_id);

  // Refs for initialization tracking
  const hasFetched = useRef<string | null>(null);

  // Date constraints
  const maxMinDates = { maxDate: dayjs(), minDate: dayjs().startOf('year') };

  /**
   * Fetches expense header and populates form
   */
  const fetchExpenseHeader = useCallback(async () => {
    if (!recordToEdit?.id) return;

    setExpensesLoading(true);
    try {
      const response = await getExpenseHeader(accessToken, recordToEdit.id);

      // Transform expense rows data
      const responseExpenseRows = response.data.expense_rows.map(
        (item: ExpenseRowItem) => {
          const formattedAmount = item.amount.toFixed(2);
          const [nokValue, oreValue] = formattedAmount.split('.');

          return {
            id: item.id,
            date: dayjs(item.date),
            title: item.title,
            amount: item.amount,
            nok: nokValue,
            ore: oreValue,
            expense_account_id: item.expense_account_id,
            file_path: item.file_path,
          };
        }
      );

      // Prepare file list for each row
      const filePathList = response.data.expense_rows.map(
        (item: ExpenseRowItem) => ({
          id: item.id,
          fileList: item.file_path
            ? [
                {
                  uid: item.id,
                  name: item.file_path.split('/').pop(),
                  url: item.file_path,
                  status: 'done',
                },
              ]
            : undefined,
        })
      );

      // Set submitter info for sent expenses via parent callback
      if (status === 'sent' && response.data.submitter) {
        setsubmitterInfo({
          firstName: response.data.submitter.first_name,
          lastName: response.data.submitter.last_name,
        });
      }

      // Update form and file state
      setFiles(filePathList);
      form.setFieldValue('expenses', responseExpenseRows);
    } catch (error) {
      console.error('Error fetching expense header:', error);
    } finally {
      setExpensesLoading(false);
    }
  }, [accessToken, recordToEdit, status, setFiles, form]);

  // Memoize initialization key
  const initializationKey = useMemo(
    () => `${recordToEdit?.id || 'new'}-${status}`,
    [recordToEdit?.id, status]
  );

  // Initialize expense data
  useEffect(() => {
    if (hasFetched.current === initializationKey) return;
    hasFetched.current = initializationKey;

    const initializeExpenseData = async () => {
      if (status !== '') {
        setLoadingExpense(true);
        await fetchExpenseHeader();
        setLoadingExpense(false);
      }
    };

    initializeExpenseData();
  }, [initializationKey, fetchExpenseHeader, status]);

  // Note: Removed automatic validation on field changes
  // Validation now only happens when user clicks save/create/submit buttons

  // Handle owner switch change with immediate form updates to prevent flicker
  const handleOwnerSwitchChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Switching TO "my expense" - save current owner and set to current user
        setPrevOwner(form.getFieldValue('owner') || prevOwner);
        form.setFieldValue('owner', profile.user_id);
      } else {
        // Switching TO "not my expense" - restore previous owner
        form.setFieldValue('owner', prevOwner);
      }
      // Call the parent handler
      onOwnerSwitchChange(checked);
    },
    [form, profile.user_id, prevOwner, onOwnerSwitchChange]
  );

  /**
   * Loading element displayed in dropdowns when data is being fetched
   */
  const loadingElement = (
    <Flex align="center" justify="center" style={{ padding: 4 }}>
      <Spin indicator={<SyncOutlined spin />} />
      <span style={{ marginLeft: 8 }}>{t('Loading')}...</span>
    </Flex>
  );

  return (
    <Spin spinning={submitting || loadingExpense || initializationLoading}>
      <Form
        form={form}
        onFieldsChange={() => setFormChanged(true)}
        name="expenseForm"
        layout="vertical"
        disabled={status === 'sent' || status === 'failed'}
      >
        <div className="aex-new-expense-form">
          {isExpenseOwnerChangeAvailable && (permissions?.isClientAdmin || permissions?.isTenantAdmin) && (
            <>
              <Form.Item
                name="is_owner"
                label={t('Are you the expense owner?')}
                initialValue={isMyExpense}
                valuePropName="checked"
              >
                <Switch
                  onChange={handleOwnerSwitchChange}
                  checked={isMyExpense}
                  disabled={
                    status === 'sent' ||
                    status === 'failed' ||
                    (profile.bank_account ? false : true)
                  }
                />
              </Form.Item>
              <Form.Item
                name="owner"
                label={t('Expense owner')}
                rules={createConditionalRules('Owner', !isMyExpense)}
                hidden={isMyExpense}
              >
                <Select
                  placeholder={t('Select an owner here')}
                  options={users.data}
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    typeof option?.label === 'string' &&
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  optionFilterProp="label"
                  optionRender={(option) => (
                    <span>
                      {option.data.email}
                      <br />
                      <i>
                        {option.data.firstName} {option.data.lastName}
                      </i>
                    </span>
                  )}
                  dropdownRender={(menu) => (
                    <div>{users.loading ? loadingElement : menu}</div>
                  )}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="external"
            label={t('Purchased with a private card?')}
            valuePropName="checked"
          >
            <Switch
              onChange={onPrivateCardChange}
              checked={isPrivateCard}
              disabled={
                status === 'sent' ||
                status === 'failed' ||
                (profile.bank_account ? false : true)
              }
            />
          </Form.Item>

          {!isPrivateCard && (
            <Form.Item
              name="card"
              label={t('Company card used')}
              rules={createRequiredOnlyRules('project')}
            >
              <SearchableSelect
                placeholder={t('Select a card here')}
                options={companyCards.data || []}
                loading={companyCards.loading}
                onSearch={(params) => debouncedFetch(FETCH_DATA_TYPES.COMPANY_CARDS, params)}
                paginationResponse={companyCards.pagination}
                disabled={status === 'sent' || status === 'failed'}
              />
            </Form.Item>
          )}

          <Form.Item
            name="purpose"
            label={t('What is purpose of expense?')}
            getValueFromEvent={(e) => cleanInput(e.target.value)}
            rules={[
              {
                required: true,
                message: t('Please enter the field!', {
                  field: t('purpose'),
                }),
              },
              () => ({
                validator(_, value) {
                  return validateInput(value, t);
                },
              }),
            ]}
          >
            <Input maxLength={255} autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="date"
            label={t('Date')}
            rules={createRequiredOnlyRules('date')}
          >
            <DatePicker
              minDate={maxMinDates.minDate}
              maxDate={maxMinDates.maxDate}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="dimension"
            label={t('Department/Responsible Unit')}
            rules={createConditionalRules(
              'Department/Responsible Unit',
              expenseSettings.require_department
            )}
          >
            <SearchableSelect
              placeholder={t('Select a department here')}
              options={dimensions.data || []}
              loading={dimensions.loading}
              onSearch={(params) => debouncedFetch(FETCH_DATA_TYPES.DIMENSIONS, params)}
              paginationResponse={dimensions.pagination}
              disabled={status === 'sent' || status === 'failed'}
            />
          </Form.Item>

          <Form.Item
            name="project"
            label={t('Project')}
            rules={createConditionalRules(
              'project',
              expenseSettings.require_project
            )}
          >
            <SearchableSelect
              placeholder={t('Select a project here')}
              options={projects.data || []}
              loading={projects.loading}
              onSearch={(params) => debouncedFetch(FETCH_DATA_TYPES.PROJECTS, params)}
              paginationResponse={projects.pagination}
              disabled={status === 'sent' || status === 'failed'}
            />
          </Form.Item>

          <Form.Item
            name="xgl"
            label="XGL"
            rules={createConditionalRules('XGL', expenseSettings.require_xgl)}
          >
            <SearchableSelect
              placeholder={t('Select XGL here')}
              options={xgl.data || []}
              loading={xgl.loading}
              onSearch={(params) => debouncedFetch(FETCH_DATA_TYPES.XGL, params)}
              paginationResponse={xgl.pagination}
              disabled={status === 'sent' || status === 'failed'}
            />
          </Form.Item>
        </div>
        <Spin spinning={expensesLoading}>
          <ExpenseEntries
            token={accessToken}
            status={status}
            headerId={headerId}
            form={form}
            files={files}
            setFiles={setFiles}
            setFormChanged={setFormChanged}
            costTypes={costTypes.data}
            costTypesLoading={costTypes.loading}
            costTypesPagination={costTypes.pagination}
            onCostTypeSearch={(params) => debouncedFetch(FETCH_DATA_TYPES.COST_TYPES, params)}
            maxMinDates={maxMinDates}
            approving={approving}
            multipleRows={files.length > 1 || expenseSettings.multiple_rows}
          />
        </Spin>
      </Form>
    </Spin>
  );
};

// Export submitter info for use in parent component
export { type ProfileInfo };
